/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import GlobalScope from '../globalScope/GlobalScope';
import FeatureEnablement from '../environment/FeatureEnablement';
import DisposableList from '../lang/DisposableList';
import IDisposable from '../lang/IDisposable';
import Subject from '../rx/Subject';
import {ILogger} from '../logger/LoggerInterface';
import LoggerFactory from '../logger/LoggerFactory';
import IPeerConnection from './IPeerConnection';
import SdpParser from './SdpParser';
import ReadOnlySubject from '../rx/ReadOnlySubject';

export interface IRtcMonitorStatistic {
  [kind: string]: IRtcStatistic;
}

export interface ILegacyRTCStatsReport {
  result: () => ILegacyRTCStatsReportItem[];
}

export interface ILegacyRTCStatsReportItem extends RTCStatsReport {
  type: string;
  names: () => string[];
  stat: (name: string) => number;
}

export interface IRtcStatistic {
  ssrc?: string;
  mediaType?: string;
  timestamp: number;
  bitrate?: number;
  bytesReceived?: number;
  framesDecoded?: number;
  packetsLost?: number;
  packetsReceived?: number;
  codec?: string;
  fps?: number;
  roundTripTime?: number;
  lastPacketReceivedTimestamp?: number;
}

const updateStatisticTimeout = 1000;
const getStatsTimeoutDuration = 5000;

export default class RtcConnectionMonitor implements IDisposable {
  private readonly _logger: ILogger = LoggerFactory.getLogger('RtcConnectionMonitor');
  private readonly _estimatedRoundTripTime: number;
  private readonly _estimatedVideoCodec: string = 'unknown';
  private readonly _estimatedAudioCodec: string = 'unknown';
  private readonly _rtcStatistic: Subject<IRtcMonitorStatistic | null> = new Subject<IRtcMonitorStatistic | null>({});
  private readonly _disposables: DisposableList = new DisposableList();
  private _peerConnection: IPeerConnection | null;
  private _isMonitorRunning = true;
  private _updateTimeout: number | null = null;
  private _tracksToMonitor: string[];
  private _monitorGeneration = 0;

  constructor(peerConnection: IPeerConnection, mediaStream: MediaStream, estimatedRoundTripTime: number, isForeground: ReadOnlySubject<boolean>) {
    this._peerConnection = peerConnection;
    this._estimatedRoundTripTime = estimatedRoundTripTime;
    this._tracksToMonitor = mediaStream.getTracks().map(track => track.kind) || [];

    if (this._peerConnection) {
      if (!FeatureEnablement.getCurrentOfferDisabled && this._peerConnection?.currentRemoteDescription?.sdp) {
        const parsedSDP = new SdpParser(this._peerConnection.currentRemoteDescription.sdp);

        this._estimatedAudioCodec = parsedSDP.audioCodec;
        this._estimatedVideoCodec = parsedSDP.videoCodec;
      }

      let isInitialSubscriptionCallback = true;

      this._disposables.add(
        isForeground.subscribe(foreground => {
          if (isInitialSubscriptionCallback) {
            isInitialSubscriptionCallback = false;

            return;
          }

          if (!foreground || !this._isMonitorRunning) {
            return;
          }

          // Invalidate any in-flight poll from before the freeze
          this._monitorGeneration++;

          if (this._updateTimeout) {
            GlobalScope.value.clearTimeout(this._updateTimeout);
            this._updateTimeout = null;
          }

          this._logger.debug('Restarting stats polling after foreground transition');
          this.updateStatistic();
        })
      );

      this.updateStatistic();
    }
  }

  get rtcStatistic(): Subject<IRtcMonitorStatistic | null> {
    return this._rtcStatistic;
  }

  public dispose(): void {
    this._monitorGeneration++;
    this._isMonitorRunning = false;
    this._peerConnection = null;
    this._disposables.dispose();

    if (this._updateTimeout) {
      GlobalScope.value.clearTimeout(this._updateTimeout);
      this._updateTimeout = null;
    }
  }

  private async updateStatistic(): Promise<void> {
    if (FeatureEnablement.getStatsPromiseBasedDisabled) {
      return this.updateStatisticLegacy();
    }

    // Safari 11 doesnt have kind or mediaType so we need to take it from the inbound-rtp object id
    const getKindForSafari11 = (item: {id: string}): string => item.id.includes('Audio') ? 'audio' : item.id.includes('Video') ? 'video' : '';
    const generation = this._monitorGeneration;
    let timeoutHandle: number | undefined;

    try {
      const statsPromise = this._peerConnection?.getStats(null);

      if (!statsPromise) {
        return;
      }

      const timeoutPromise = new Promise<null>(resolve => {
        timeoutHandle = GlobalScope.value.setTimeout(() => resolve(null), getStatsTimeoutDuration);
      });
      const stats = await Promise.race([statsPromise, timeoutPromise]);

      if (generation !== this._monitorGeneration || !this._isMonitorRunning) {
        return;
      }

      if (!stats) {
        this._logger.debug('getStats() timed out, skipping stats processing');
      } else {
        const rtcStatistics: IRtcMonitorStatistic = {};
        let roundTripTime = this._estimatedRoundTripTime;
        let lastPacketReceivedTimestamp: number | undefined;

        stats.forEach(report => {
          if (report.type === 'candidate-pair' && report.currentRoundTripTime) {
            roundTripTime = report.currentRoundTripTime * 1000;

            if (rtcStatistics['audio']) {
              rtcStatistics['audio'].roundTripTime = roundTripTime;
            }

            if (rtcStatistics['video']) {
              rtcStatistics['video'].roundTripTime = roundTripTime;
            }
          }

          if (report.type === 'candidate-pair' && report.lastPacketReceivedTimestamp) {
            lastPacketReceivedTimestamp = report.lastPacketReceivedTimestamp;

            if (rtcStatistics['audio']) {
              rtcStatistics['audio'].lastPacketReceivedTimestamp = lastPacketReceivedTimestamp;
            }

            if (rtcStatistics['video']) {
              rtcStatistics['video'].lastPacketReceivedTimestamp = lastPacketReceivedTimestamp;
            }
          }

          if (report.type === 'inbound-rtp') {
            const kind = report.kind || report.mediaType || getKindForSafari11(report);
            let codec = '';

            stats.forEach(item => {
              if (item.id === report.codecId) {
                codec = item.mimeType;
              }
            });

            if (kind && this._tracksToMonitor.includes(kind)) {
              const bytes = report.bytesReceived || 0;
              const previousBytesReceived = this._rtcStatistic.value?.[kind]?.bytesReceived ?? 0;
              const bytesDelta = bytes - previousBytesReceived;
              const timeDelta = report.timestamp - (this._rtcStatistic.value?.[kind]?.timestamp || 0);
              const bitrate = bytesDelta && timeDelta ? Math.floor(8 * bytesDelta /
                timeDelta * 1000) : 0;

              rtcStatistics[kind] = {
                ssrc: report.ssrc,
                mediaType: kind,
                timestamp: report.timestamp,
                bitrate,
                bytesReceived: report.bytesReceived,
                packetsLost: report.packetsLost,
                packetsReceived: report.packetsReceived,
                codec: codec || this.getCodecByType(kind),
                roundTripTime
              };

              if (report.lastPacketReceivedTimestamp || lastPacketReceivedTimestamp) {
                rtcStatistics[kind].lastPacketReceivedTimestamp = report.lastPacketReceivedTimestamp || lastPacketReceivedTimestamp;
              }

              if (kind === 'video' && this._rtcStatistic.value?.video?.framesDecoded) {
                rtcStatistics[kind]['framesDecoded'] = report.framesDecoded;

                const fps = (report.framesDecoded - this._rtcStatistic.value.video.framesDecoded) /
                  (rtcStatistics[kind].timestamp - this._rtcStatistic.value.video.timestamp) *
                  1000;

                rtcStatistics[kind]['fps'] = fps ? Math.round(fps * 100) / 100 : 0;
              }
            }
          }
        });

        this._rtcStatistic.value = rtcStatistics;
      }
    } catch (error) {
      if (generation !== this._monitorGeneration || !this._isMonitorRunning) {
        return;
      }

      this._logger.warn('getStats() rejected', error);
    } finally {
      GlobalScope.value.clearTimeout(timeoutHandle);
    }

    if (this._isMonitorRunning && generation === this._monitorGeneration) {
      this._updateTimeout = GlobalScope.value.setTimeout(
        () => this.updateStatistic(), updateStatisticTimeout);
    }
  }

  private async updateStatisticLegacy(): Promise<void> {
    const generation = this._monitorGeneration;
    let timeoutHandle: number | undefined;

    try {
      const statsPromise = this._peerConnection?.getStatsLegacy();

      if (!statsPromise) {
        return;
      }

      const timeoutPromise = new Promise<null>(resolve => {
        timeoutHandle = GlobalScope.value.setTimeout(() => resolve(null), getStatsTimeoutDuration);
      });
      const stats = await Promise.race([statsPromise, timeoutPromise]);

      if (generation !== this._monitorGeneration || !this._isMonitorRunning) {
        return;
      }

      if (!stats) {
        this._logger.debug('getStatsLegacy() timed out, skipping stats processing');
      } else {
        const rtcStatistics: IRtcMonitorStatistic = {};
        let roundTripTime = this._estimatedRoundTripTime;

        stats.result().forEach(report => {
          report.names().forEach(name => {
            if (name === 'googRTT') {
              roundTripTime = report.stat(name);
            }
          });

          if (report.type === 'ssrc') {
            const mediaType = report.stat('mediaType').toString();

            rtcStatistics[mediaType] = {timestamp: Date.now()};
            rtcStatistics[mediaType]['ssrc'] = report.stat('ssrc').toString();
            rtcStatistics[mediaType]['mediaType'] = mediaType;
            rtcStatistics[mediaType]['bytesReceived'] = report.stat('bytesReceived');
            rtcStatistics[mediaType]['packetsLost'] = report.stat('packetsLost');
            rtcStatistics[mediaType]['packetsReceived'] = report.stat('packetsReceived');
            rtcStatistics[mediaType]['codec'] = report.stat('googCodecName').toString() || this.getCodecByType(mediaType);
            rtcStatistics[mediaType]['roundTripTime'] = roundTripTime;

            if (mediaType === 'video' && this._rtcStatistic.value?.video?.framesDecoded) {
              rtcStatistics[mediaType]['framesDecoded'] = report.stat('framesDecoded');
              rtcStatistics[mediaType]['fps'] = (rtcStatistics[mediaType].framesDecoded - this._rtcStatistic.value.video.framesDecoded) /
                (rtcStatistics[mediaType].timestamp - this._rtcStatistic.value.video.timestamp) *
              1000 || 0;
            }
          }
        });

        this._rtcStatistic.value = rtcStatistics;
      }
    } catch (error) {
      if (generation !== this._monitorGeneration || !this._isMonitorRunning) {
        return;
      }

      this._logger.warn('getStatsLegacy() rejected', error);
    } finally {
      GlobalScope.value.clearTimeout(timeoutHandle);
    }

    if (this._isMonitorRunning && generation === this._monitorGeneration) {
      this._updateTimeout = GlobalScope.value.setTimeout(
        () => this.updateStatistic(), updateStatisticTimeout);
    }
  }

  private getCodecByType(type: string): string {
    switch (type) {
      case 'audio':
        return this._estimatedAudioCodec || 'unknown';
      case 'video':
        return this._estimatedVideoCodec || 'unknown';
      default:
        return 'unknown';
    }
  }
}